import { useTheme } from "@emotion/react";
import { ExpandMore, WhatsApp } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import api from "../../../../../../services/api";
import useStyles from "./styles";

const mensagemErro = (error, padrao) =>
  error?.response?.data?.error || error?.response?.data?.message || padrao;

export default function ConfigBotWhatsApp() {
  const theme = useTheme();
  const styles = useStyles(theme);
  // Busca só depois da primeira abertura do accordion.
  const [jaAberto, setJaAberto] = useState(false);

  const [snackbar, setSnackbar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [confirmarExclusao, setConfirmarExclusao] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [grupoSalvo, setGrupoSalvo] = useState("");
  const [grupoId, setGrupoId] = useState("");

  const aplicar = (dados) => {
    setGrupoSalvo(dados?.grupoId || "");
    setGrupoId(dados?.grupoId || "");
  };

  useEffect(() => {
    if (!jaAberto) return;
    (async () => {
      setLoading(true);
      try {
        const response = await api.post("/interno/find/configBotWhatsApp");
        aplicar(response?.data);
      } catch (error) {
        console.log(error);
        setSnackbar({
          children: mensagemErro(
            error,
            "Erro ao buscar a configuração do bot de WhatsApp!",
          ),
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [jaAberto]);

  const handleCancel = () => {
    setIsEdit(false);
    setGrupoId(grupoSalvo);
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const response = await api.post("/interno/update/configBotWhatsApp", {
        grupoId: grupoId.trim(),
      });
      aplicar(response?.data);
      setIsEdit(false);
      setSnackbar({
        children: "Grupo de WhatsApp atualizado com sucesso!",
        severity: "success",
      });
    } catch (error) {
      console.log(error);
      setSnackbar({
        children: mensagemErro(error, "Erro ao salvar o grupo de WhatsApp!"),
        severity: "error",
      });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    setSalvando(true);
    try {
      const response = await api.post("/interno/delete/configBotWhatsApp");
      aplicar(response?.data);
      setIsEdit(false);
      setConfirmarExclusao(false);
      setSnackbar({
        children: "Configuração do bot de WhatsApp excluída!",
        severity: "success",
      });
    } catch (error) {
      console.log(error);
      setSnackbar({
        children: mensagemErro(error, "Erro ao excluir a configuração!"),
        severity: "error",
      });
    } finally {
      setSalvando(false);
    }
  };

  const alterado = grupoId.trim() && grupoId.trim() !== grupoSalvo;

  return (
    <>
      <Accordion
        data-secao="bot-whatsapp"
        onChange={(_, aberto) => aberto && setJaAberto(true)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Box sx={styles.containerHeader}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WhatsApp color="primary" />
              <Typography variant="h5" sx={{ ...styles.title }}>
                Configurações do bot de WhatsApp
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Paper variant="outlined" sx={styles.paperBody}>
            {loading ? (
              <Box sx={styles.loading}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Grupo de alertas de instabilidade
                  </Typography>
                  <Typography sx={styles.subtitle}>
                    Grupo de WhatsApp para onde o bot envia os alertas de
                    instabilidade da API ou do banco de dados.
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  variant="filled"
                  label="ID do grupo de WhatsApp"
                  data-busca="bot-grupo"
                  placeholder={isEdit ? "Ex.: 120363000000000000@g.us" : "Não configurado"}
                  InputLabelProps={{ shrink: true }}
                  value={grupoId}
                  onChange={(e) => setGrupoId(e.target.value)}
                  disabled={!isEdit || salvando}
                  sx={{ maxWidth: 520 }}
                />
              </>
            )}
          </Paper>

          {!loading && (
            <Box sx={styles.boxActions}>
              {!isEdit ? (
                <Box sx={styles.boxEditActions}>
                  {grupoSalvo && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setConfirmarExclusao(true)}
                      disabled={salvando}
                    >
                      Excluir
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setIsEdit(true)}
                  >
                    Editar
                  </Button>
                </Box>
              ) : (
                <Box sx={styles.boxEditActions}>
                  <Button
                    onClick={handleCancel}
                    size="small"
                    variant="outlined"
                    disabled={salvando}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={salvar}
                    disabled={!alterado || salvando}
                    startIcon={
                      salvando && <CircularProgress size={14} color="inherit" />
                    }
                  >
                    Salvar
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={confirmarExclusao}
        onClose={() => !salvando && setConfirmarExclusao(false)}
      >
        <DialogTitle>Excluir configuração</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            O bot deixará de enviar alertas de instabilidade para o grupo de
            WhatsApp. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmarExclusao(false)}
            disabled={salvando}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={excluir}
            disabled={salvando}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
